import { Formik } from "formik";
import SimpleDocUploader from "lib/components/common/docs/simple-doc-uploader";
import ErrorBox from "lib/components/common/error-box";
import SimpleCard from "lib/components/common/simple-card";
import SimpleSwitch from "lib/components/common/simple-switch";
import SimpleTextbox from "lib/components/common/simple-textbox";
import { REQUIRED_FIELD_VALIDATION_SCHEMA } from "lib/constants/validation-constants";
import { FC, useRef, useState } from "react";
import { Row, Col, Button, Form } from "react-bootstrap";
import { toast } from "sonner";

interface TherapyDetailsProps {
  error?: string;
  isLoading?: boolean;
  therapy: Therapy;
  updateTherapy: (therapy: Therapy) => void;
  onCancelClick?: () => void;
  addTherapy?: (therapy: Therapy) => void;
  deleteTherapy?: (therapyId: string) => void;
}

const TherapyDetails: FC<TherapyDetailsProps> = ({
  error,
  isLoading,
  therapy,
  onCancelClick,
  updateTherapy,
  deleteTherapy,
  addTherapy,
}) => {
  const [therapyImage, setTherapyImage] = useState<File[] | null>(
    therapy.image ? [therapy.image] : null
  );

  const [therapyImageUrl, setTherapyImageUrl] = useState<string | null>(
    therapy?.imageURL
  );

  const formikRef = useRef(null);

  return (
    <SimpleCard
      title="Therapy Details"
      isLoading={isLoading}
      action={
        <Button
          type="button"
          variant="outline-primary"
          className="px-4"
          onClick={() => {
            formikRef.current?.submitForm();
          }}
        >
          Save therapy
        </Button>
      }
      isShowBackButton
      onBackButtonClick={onCancelClick}
      error={error}
    >
      <Row>
        <Col sm="3">
          <SimpleDocUploader
            mode="single"
            doc={therapyImage?.[0]}
            docUrl={therapyImageUrl}
            onChange={setTherapyImage}
            onDelete={() => {
              setTherapyImage(null);
              setTherapyImageUrl(null);
            }}
          />
        </Col>
        <Col sm="9">
          <Formik
            initialValues={
              {
                ...therapy,
                isMedical: therapy.isMedical,
                isShowOnWebsite: therapy.isShowOnWebsite,
                expiryDate: therapy.expiryDate
                  ? therapy.expiryDate.split("T")[0]
                  : null,
              } as Therapy
            }
            // validate={(values) => {
            //   const errors: any = {};
            //   if (!values.name) {
            //     errors.name = "Please enter therapy name";
            //   }
            //   if (!values.description) {
            //     errors.description = "Please describe the therapy";
            //   }

            //   return errors;
            // }}
            onSubmit={(values: Therapy, { setSubmitting }) => {
              // const formattedValues = {
              //   ...values,
              //   expiryDate: values.expiryDate === "" ? null : values.expiryDate,
              // };

              if (!therapyImage && !therapyImageUrl) {
                toast.error("Please select therapy image");
                return;
              }

              if (!values.name) {
                toast.error("Please enter therapy name");
                return;
              }

              if (!values.description) {
                toast.error("Please describe the therapy");
                return;
              }

              if (!therapy.id) {
                addTherapy({ ...values, image: therapyImage?.[0] });
              } else {
                updateTherapy({ ...values, image: therapyImage?.[0] });
              }
              setSubmitting(false);
            }}
            innerRef={formikRef}
          >
            {({ values, handleChange, errors, setValues }) => (
              <Form>
                <SimpleTextbox
                  label="Name"
                  type="text"
                  name="name"
                  value={values.name}
                  className="form-control"
                  onChange={handleChange}
                  required
                  validationSchema={REQUIRED_FIELD_VALIDATION_SCHEMA(
                    "Therapy name is required"
                  )}
                />

                <SimpleTextbox
                  label="Description"
                  as="textarea"
                  name="description"
                  value={values.description}
                  className="form-control"
                  onChange={handleChange}
                  required
                  validationSchema={REQUIRED_FIELD_VALIDATION_SCHEMA(
                    "Therapy description is required"
                  )}
                />

                <SimpleSwitch
                  label="Is Medical"
                  name="isMedical"
                  value={values.isMedical}
                  onChange={handleChange}
                  isShowIcons={false}
                />

                <SimpleSwitch
                  label="Show on website"
                  name="isShowOnWebsite"
                  value={values.isShowOnWebsite}
                  onChange={handleChange}
                  isShowIcons={false}
                />

                <SimpleSwitch
                  label="Session Booking Enabled"
                  name="isSessionBookingEnabled"
                  value={values.isSessionBookingEnabled}
                  onChange={handleChange}
                  isShowIcons={false}
                />

                <SimpleTextbox
                  label="Credits"
                  type="text"
                  name="credits"
                  value={values.credits}
                  className="form-control"
                  onChange={handleChange}
                />

                <SimpleTextbox
                  label="Price"
                  type="text"
                  name="price"
                  value={values.price}
                  className="form-control"
                  onChange={handleChange}
                />

                <SimpleTextbox
                  label="Duration (in minutes)"
                  type="text"
                  name="duration"
                  value={values.duration}
                  className="form-control"
                  onChange={handleChange}
                />

              </Form>
            )}
          </Formik>
        </Col>
        {therapy.id && (
        <Col sm="12">
          <hr />
          <Button
            type="button"
            variant="outline-danger"
            className="px-4 text-danger mr-1"
            onClick={deleteTherapy?.bind(null, therapy.id)}>
            Delete therapy
          </Button>
        </Col>
        )}
      </Row>
    </SimpleCard>
  );
};
export default TherapyDetails;
